import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { PageContainer } from "../../components/page_container/PageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import FaviconGeneratorImage from "../../assets/FaviconGenerator.webp";
import {
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import { Box } from "@mui/system";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  addPdfAsync,
  mergePdfDocumentsAsync,
  removeDocument,
  selectState,
} from "./PdfMergerSlice";
import FileSaveIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { HomeCard } from "../../components/home_card/HomeCard";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export function PdfMerger() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("pdfMerger.title")}
      pageSeoTitle={t("pdfMerger.seoTitle")}
      pageDescription={t("pdfMerger.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("pdfMerger.mainHeading")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <b style={{ color: "#FFD9A8" }}>{t("pdfMerger.step1")}</b> {t("pdfMerger.step1Description")}
              <br />
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadIcon sx={{ color: "#FFD9A8" }} />}
                onChange={(e: any) => {
                  const files = e.target.files;
                  if (files) {
                    for (let i = 0; i < files.length; i++) {
                      dispatch(addPdfAsync(files[i]));
                    }
                  }
                }}
              >
                <Typography>{t("pdfMerger.selectButton")}</Typography>
                <VisuallyHiddenInput
                  multiple
                  type="file"
                  accept="application/pdf"
                />
              </Button>
            </Stack>
          </Grid2>
          <Grid2 xs={12}>
            <Typography
              sx={{
                mb: 1,
                display:
                  state.pdfDocumentsToMerge.length >= 1 ? "flex" : "none",
              }}
            >
              {t("pdfMerger.selectedFiles")}
            </Typography>
            {state.pdfDocumentsToMerge.map((doc, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography sx={{ width: 40 }}>{index + 1} - </Typography>

                <Typography sx={{ width: 300 }}>{doc.name}</Typography>

                <IconButton onClick={() => dispatch(removeDocument(doc))}>
                  <DeleteIcon sx={{ color: "#FFD9A8" }} />
                </IconButton>
              </Box>
            ))}
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <b style={{ color: "#FFD9A8" }}>{t("pdfMerger.step2")}</b> {t("pdfMerger.step2Description")}
              <br />
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                sx={{
                  display:
                    state.pdfDocumentsToMerge.length < 2 ? "flex" : "none",
                }}
              >
                {t("pdfMerger.selectAtLeast2")}
              </Typography>
              <Button
                sx={{
                  display:
                    state.pdfDocumentsToMerge.length >= 2 ? "flex" : "none",
                }}
                component="label"
                variant="contained"
                startIcon={<FileSaveIcon sx={{ color: "#FFD9A8" }} />}
                onClick={(e: any) => dispatch(mergePdfDocumentsAsync())}
              >
                <Typography>{t("pdfMerger.mergeButton")}</Typography>
              </Button>
            </Stack>
          </Grid2>
          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>
          <Grid2 xs={12}>
            <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("pdfMerger.content.mergeAnyHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("pdfMerger.content.mergeAnyDescription")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("pdfMerger.content.privacyHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("pdfMerger.content.privacyDescription")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("pdfMerger.content.howToHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("pdfMerger.content.howToDescription")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("pdfMerger.related")}
            </Typography>
            <Grid2 xs={3}>
              <HomeCard
                languageResource="faviconGenerator"
                link="/FaviconGenerator"
                smallCard={true}
                image={FaviconGeneratorImage}
              />
            </Grid2>
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
